import invert from 'lodash/invert';
import {
  AggExprTypes, CastTargetTypes, ExpressionTypes, resolveCastTargetType,
} from 'core/constants/report-expressions';
import {
  ColumnDataTypes,
  ColumnDataTypesList,
  ColumnModes,
  ColumnRenderTypes,
  DateColumnDataTypesList,
  DimensionMode,
  DimensionStatus,
  FieldEntitiesType,
  NumericColumnDataTypesList,
  StringColumnDataTypesList,
  ZeroAnchorStatus,
} from 'core/constants/report';
import { IColumn, IColumnFormatter } from 'core/models/report-response';
import {
  IColumnExprPart,
  IExpression,
  IExpressionGuarded,
  isAnd,
  isAny,
  isAnyHeavy,
  isAnyLast,
  isArgMax,
  isArgMaxIf,
  isArgMin,
  isArgMinIf,
  isArrayIndexOfFn,
  isArrayJoinFn,
  isAvg,
  isAvgIf,
  isColumn,
  isComp,
  isConcat,
  isConfig,
  isConstant,
  isCustomFilterVariable,
  isCount,
  isCountDistinct,
  isCountDistinctIf,
  isCountIf,
  isDate,
  isDateDiffFn,
  isDateFn,
  isDivide,
  isFirstValue,
  isGroupConcat,
  isGroupConcatDistinct,
  isIf,
  isIsNull,
  isJSONExtract,
  isLag,
  isLastValue,
  isLead,
  isMax,
  isMaxIf,
  isMin,
  isMinIf,
  isMinus,
  isMultiply,
  isNot,
  isNull,
  isOr,
  isPlus,
  isPosition,
  isRank,
  isRegexpExtract,
  isReplace, isReplaceRegexp,
  isRowNumber,
  isSum,
  isSumIf,
  isCastFn,
  newColumnExpr,
  newCountExpr,
  newMaxExpr,
  newMinExpr,
  newSumExpr,
} from 'core/models/report-expressions';
import { isValidArray, isValidObjectAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';
import { DateBinningFormatters, FormatterMetaData, Formatters } from 'core/constants/formatters';
import { IDimension } from 'core/models/report-redux';
import { isDrilldownPossible } from './report.util';

export const aggDisplayNameTypes = { // display name to types lookup
  Sum: AggExprTypes.Sum,
  Max: AggExprTypes.Max,
  Min: AggExprTypes.Min,
  Count: AggExprTypes.Count,
  'Count Unique': AggExprTypes.CountDistinct,
};
export const aggTypeDisplayNames = invert(aggDisplayNameTypes) as { [p in AggExprTypes]: keyof typeof aggDisplayNameTypes };
export const aggDisplayNames = Object.values(aggTypeDisplayNames);

export const buildMeasureColumn = (
  id:string,
  name: string,
  Alias: string,
  DataType: ColumnDataTypes,
  aggregator: keyof typeof aggDisplayNameTypes,
  measure: IColumn,
  Entity: string,
  EntityName: string,
  SchemaName: string,
  joinID: string,
  formatter?: IColumnFormatter,
  Namespace?: string,
  QueryId?: string,
  IsSourceCustomColumn?: boolean,
  NamespaceEntity?: string,
  NsDisplayName?: string,
  description?: string,
): IColumn => {
  const columnExprNew = newColumnExpr(name, Alias, DataType, joinID || Namespace, Entity); // Expression SchemaName is the Alias of the builder config
  let newExpr: IExpression<ExpressionTypes.Aggregation>;
  switch (aggregator) {
    case 'Sum':
      newExpr = newSumExpr(columnExprNew);
      break;
    case 'Max':
      newExpr = newMaxExpr(columnExprNew);
      break;
    case 'Min':
      newExpr = newMinExpr(columnExprNew);
      break;
    case 'Count':
      newExpr = newCountExpr(columnExprNew);
      break;
    case 'Count Unique':
      newExpr = newCountExpr(columnExprNew, true);
      break;
    default:
      throw new Error(`unhandled aggregation ${aggregator}`);
  }

  return {
    ...measure,
    Name: name,
    Id: id,
    BuilderConfig: {
      ...measure.BuilderConfig,
      DisplayName: name,
      Entity,
      EntityName,
      SchemaName,
      Alias,
      JoinID: joinID,
      DataType,
      NamespaceEntity,
      NsDisplayName,
      ...(Entity === 'Custom' ? { uniqueKey: SchemaName } : {}),

    },
    Props: {
      ...measure.Props,
      AllowDrillDown: isDrilldownPossible(newExpr),
      Description: description,
      Formatter: isValidObjectAndNotEmpty(formatter) ? formatter : measure.Props.Formatter,
      Namespace,
      Measure: {
        ...measure.Props.Measure,
        Name: name,
        Entity,
        JoinID: joinID,
        Expression: {
          ...newExpr,
        },
        NamespaceEntity,
        NsDisplayName,
      },
      IsSourceCustomColumn,
      QueryId,
    },
  };
};
export const defaultCustomMeasureFormatter :IColumnFormatter = {
  FormatterType: Formatters.NUMBER,
  FormatterMeta: {
    [FormatterMetaData.Decimal]: 0,
    [FormatterMetaData.Seperator]: 1000,
  },
};
export const buildCustomMeasure = (name: string, mode: ColumnModes, expr: IExpression, uniqueId: string | number, formatter: IColumnFormatter, dataType:ColumnDataTypes, renderType?: ColumnRenderTypes, isSourceCustomColumn?:boolean, description?: string): IColumn => <IColumn>({
  Name: name,
  Id: uniqueId, // Column Id
  Props: {
    Mode: mode,
    AllowDrillDown: isDrilldownPossible(expr),
    DrilldownEnabled: isDrilldownPossible(expr),
    Measure: {
      Name: name,
      IsMeasure: true,
      IsCustomExpression: true,
      Expression: expr,
      RenderType: renderType,
    },
    Formatter: isValidObjectAndNotEmpty(formatter) ? formatter : defaultCustomMeasureFormatter,
    IsSourceCustomColumn: isSourceCustomColumn,
    Description: description,
  },
  BuilderConfig: {
    Alias: name,
    DisplayName: name,
    DataType: dataType,
    Entity: FieldEntitiesType.Custom,
    uniqueKey: uniqueId,
  },
});

export const buildCustomDimension = (name: string,
  mode: ColumnModes,
  expr: IExpression | string,
  uniqueId: string | number,
  dataType:ColumnDataTypes,
  dimensionType: DimensionMode,
  description?: string,
  formatter?: IColumnFormatter): IDimension => <IDimension>({
    Name: name,
    Id: uniqueId,
    IsCustomExpression: true,
    Expression: expr,
    Applied: DimensionStatus.Applied,
    ZeroAnchor: ZeroAnchorStatus.Disabled,
    Orderable: true,
    DimensionMode: dimensionType,
    Mode: mode,
    Props: {
      Mode: mode,
      AllowDrillDown: false,
      Dimension: {
        Name: name,
        Applied: DimensionStatus.Applied,
        ZeroAnchor: ZeroAnchorStatus.Disabled,
        Orderable: true,
        DimensionMode: dimensionType,
        Expression: expr,
        IsCustomExpression: true,
      },
      Formatter: formatter ?? null,
      Description: description,
    },
    BuilderConfig: {
      SchemaName: name,
      Alias: name,
      DisplayName: name,
      DataType: dataType,
      Entity: FieldEntitiesType.Custom,
      uniqueKey: uniqueId,
    },
  });

export const findUnderlyingColumn = (e: IExpression): IColumnExprPart => {
  if (!e) return null;
  const expr = e as IExpressionGuarded;
  if (isColumn(expr)) return expr.Part;
  if (isConstant(expr) || isConfig(expr) || isNull(expr) || isRowNumber(expr) || isRank(expr)) return null;
  if (isFirstValue(expr) || isLastValue(expr) || isLead(expr) || isLag(expr)) return findUnderlyingColumn(expr.Part.Expression);
  if (isSum(expr)) return findUnderlyingColumn(expr.Part.ExpressionToSum);
  if (isSumIf(expr)) return findUnderlyingColumn(expr.Part.ExpressionToSum);
  if (isMax(expr)) return findUnderlyingColumn(expr.Part.MaxOf);
  if (isMaxIf(expr)) return findUnderlyingColumn(expr.Part.ExpressionToMax);
  if (isMin(expr)) return findUnderlyingColumn(expr.Part.MinOf);
  if (isMinIf(expr)) return findUnderlyingColumn(expr.Part.ExpressionToMin);
  if (isAvg(expr)) return findUnderlyingColumn(expr.Part.ExpressionToAvg);
  if (isAvgIf(expr)) return findUnderlyingColumn(expr.Part.ExpressionToAvg);
  if (isCount(expr)) return findUnderlyingColumn(expr.Part.ElementToCount);
  if (isCountIf(expr)) return null; // TODO: Build and return new column
  if (isCountDistinct(expr)) return findUnderlyingColumn(expr.Part.ExpressionToCount);
  if (isCountDistinctIf(expr)) return findUnderlyingColumn(expr.Part.ExpressionToCount);
  if (isArgMax(expr)) return findUnderlyingColumn(expr.Part.Target);
  if (isArgMin(expr)) return findUnderlyingColumn(expr.Part.Target);
  if (isArgMaxIf(expr)) return findUnderlyingColumn(expr.Part.Target);
  if (isArgMinIf(expr)) return findUnderlyingColumn(expr.Part.Target);
  if (isAny(expr)) return findUnderlyingColumn(expr.Part.Expression);
  if (isAnyHeavy(expr)) return findUnderlyingColumn(expr.Part.Expression);
  if (isAnyLast(expr)) return findUnderlyingColumn(expr.Part.Expression);
  if (isGroupConcat(expr) || isGroupConcatDistinct(expr)) return findUnderlyingColumn(expr.Part.ExpressionToConcat);
  if (isPlus(expr) || isMinus(expr) || isMultiply(expr) || isDivide(expr)) return null;
  if (isComp(expr)) return null;
  if (isAnd(expr) || isOr(expr)) return null;
  if (isNot(expr) || isIsNull(expr)) return findUnderlyingColumn(expr.Part.Expression);
  if (isDate(expr) && !isDateDiffFn(expr)) return findUnderlyingColumn(expr.Part.DateTime);
  if (isDateDiffFn(expr)) return null;
  if (isPosition(expr)) return findUnderlyingColumn(expr.Part.Haystack);
  if (isConcat(expr)) {
    const underlyingColumnsAll = expr.Part.Items.map((field) => findUnderlyingColumn(field));
    const firstNonNullColumn = underlyingColumnsAll.find((column) => column !== null);
    return firstNonNullColumn;
  }
  if (isJSONExtract(expr)) return findUnderlyingColumn(expr.Part.JSON);
  if (isRegexpExtract(expr)) return findUnderlyingColumn(expr.Part.Haystack);
  if (isReplace(expr) || isReplaceRegexp(expr)) return findUnderlyingColumn(expr.Part.Haystack);
  if (isArrayJoinFn(expr)) return findUnderlyingColumn(expr.Part.Array);
  if (isArrayIndexOfFn(expr)) return findUnderlyingColumn(expr.Part.Array);
  if (isIf(expr)) return findUnderlyingColumn(expr.Part.Condition);
  // A custom filter variable placeholder is a leaf with no backing column.
  if (isCustomFilterVariable(expr)) return null;
  if (isCastFn(expr)) return findUnderlyingColumn(expr.Part.Expression);

  // noinspection UnnecessaryLocalVariableJS
  /**
   * Type-exhaustiveness check breaks if a newly added expression type/subtype is not handled by this function.
   * If it is breaking, fix it by creating its own `isType(expr)` function and calling it here.
   * See https://www.typescriptlang.org/docs/handbook/2/narrowing.html#exhaustiveness-checking.
   */
  const _exhaustiveCheck: never = expr;
  return _exhaustiveCheck;
};

// function to get the formatType for a custom measure given its dataType
// @exprDataType: dataType for a custom measure
// returns: formatType for a custom measure
export const getFormatterTypeForDataType = (exprDataType: ColumnDataTypes) :Formatters => {
  if (NumericColumnDataTypesList.includes(exprDataType)) {
    return Formatters.NUMBER;
  }
  if (DateColumnDataTypesList.includes(exprDataType)) {
    if (ColumnDataTypesList.DateTime === exprDataType) {
      return Formatters.DATE; // since dateTime is not currently supported
    }
    return Formatters.TIME;
  }

  if (StringColumnDataTypesList.includes(exprDataType)) {
    return Formatters.STRING;
  }
  return Formatters.NUMBER;
};

// to get the dataType of the column given an expression
// @exp: expression part of a custom measure
// returns data type of the column
export const getUnderlyingColumnDateType = (exp:IExpression): ColumnDataTypes => {
  const underlyingColumn = findUnderlyingColumn(exp);
  return underlyingColumn?.Column?.DataType ?? ColumnDataTypes.Int;
};

// to get dataType for a given expression object of a custom measure that is mapped to a formatType.
// @expr: expression object of a custom measure
// returns: formatType for a custom measure
export const getFormattingDataTypeFromCustomExp = (expr: IExpression): Formatters => {
  switch (getDataTypeFromCustomColumn(expr)) {
    default:
    case ColumnDataTypes.Int:
    case ColumnDataTypes.Boolean:
      return Formatters.NUMBER;
    case ColumnDataTypes.String:
      return Formatters.STRING;
    case ColumnDataTypes.DateTime:
      return Formatters.DATE;
    case ColumnDataTypes.Time:
      return Formatters.TIME;
  }
};

// function to identify the dataType of a custom dimension or measure
// @expr: expression object of a custom dimension or measure
// returns: dataType for a custom dimension or measure
export const getDataTypeFromCustomColumn = (e: IExpression): ColumnDataTypes => {
  if (!e) return ColumnDataTypes.Int;
  const expr = e as IExpressionGuarded;

  if (isConstant(expr)) return expr.Part.DataType;
  if (isColumn(expr)) return getUnderlyingColumnDateType(expr);

  if (isSum(expr) || isSumIf(expr) || isAvg(expr) || isAvgIf(expr) || isCount(expr) || isCountIf(expr) || isCountDistinct(expr)
    || isCountDistinctIf(expr) || isPlus(expr) || isMinus(expr) || isMultiply(expr) || isDivide(expr)) return ColumnDataTypes.Int;
  if (isConfig(expr) || isNull(expr) || isRowNumber(expr) || isRank(expr)) return ColumnDataTypes.Int;
  if (isComp(expr)) return ColumnDataTypes.Boolean;
  if (isAnd(expr) || isOr(expr)) return ColumnDataTypes.Boolean;
  if (isNot(expr) || isIsNull(expr)) return ColumnDataTypes.Boolean;
  if (isDate(expr)) {
    if (isDateDiffFn(expr)) return ColumnDataTypes.Int;
    if (isDateFn(expr)) return getDataTypeFromCustomColumn(expr.Part.DateTime);
    switch (expr.SubType) { // isDate(expr)
      default:
      case DateBinningFormatters.QUARTER:
      case DateBinningFormatters.QUARTEROFTHEYEAR:
      case DateBinningFormatters.DATE:
      case DateBinningFormatters.DATE_YEAR:
      case DateBinningFormatters.MONTH:
      case DateBinningFormatters.MONTHOFTHEYEAR:
        return ColumnDataTypes.String;
      case DateBinningFormatters.HOUROFTHEDAY:
        return ColumnDataTypes.Time;
      case DateBinningFormatters.NONE:
        return ColumnDataTypes.DateTime;
      case DateBinningFormatters.DAY:
      case DateBinningFormatters.DAY_OF_THE_YEAR:
      case DateBinningFormatters.DAY_OF_THE_MONTH:
      case DateBinningFormatters.YEAR:
      case DateBinningFormatters.WEEK:
      case DateBinningFormatters.WEEKOFTHEYEAR:
        return ColumnDataTypes.Int;
    }
  }

  if (isFirstValue(expr) || isLastValue(expr) || isLead(expr) || isLag(expr)) return getDataTypeFromCustomColumn(expr.Part.Expression);
  if (isMax(expr)) return getDataTypeFromCustomColumn(expr.Part.MaxOf);
  if (isMaxIf(expr)) return getDataTypeFromCustomColumn(expr.Part.ExpressionToMax);
  if (isMin(expr)) return getDataTypeFromCustomColumn(expr.Part.MinOf);
  if (isMinIf(expr)) return getDataTypeFromCustomColumn(expr.Part.ExpressionToMin);
  if (isArgMax(expr)) return getDataTypeFromCustomColumn(expr.Part.Target);
  if (isArgMin(expr)) return getDataTypeFromCustomColumn(expr.Part.Target);
  if (isArgMaxIf(expr)) return getDataTypeFromCustomColumn(expr.Part.Target);
  if (isArgMinIf(expr)) return getDataTypeFromCustomColumn(expr.Part.Target);
  if (isAny(expr)) return getDataTypeFromCustomColumn(expr.Part.Expression);
  if (isAnyHeavy(expr)) return getDataTypeFromCustomColumn(expr.Part.Expression);
  if (isAnyLast(expr)) return getDataTypeFromCustomColumn(expr.Part.Expression);
  if (isGroupConcat(expr) || isGroupConcatDistinct(expr)) return ColumnDataTypes.String;
  if (isPosition(expr)) return getDataTypeFromCustomColumn(expr.Part.Haystack);
  if (isConcat(expr)) return ColumnDataTypes.String;
  if (isJSONExtract(expr)) return getDataTypeFromCustomColumn(expr.Part.JSON);
  if (isRegexpExtract(expr)) return getDataTypeFromCustomColumn(expr.Part.Haystack);
  if (isReplace(expr) || isReplaceRegexp(expr)) return getDataTypeFromCustomColumn(expr.Part.Haystack);
  if (isArrayJoinFn(expr)) return getDataTypeFromCustomColumn(expr.Part.Array);
  if (isArrayIndexOfFn(expr)) return getDataTypeFromCustomColumn(expr.Part.Array);
  if (isIf(expr)) return getDataTypeFromCustomColumn(expr.Part.ThenPart);
  // The placeholder carries its (informational) data type on its Part.
  if (isCustomFilterVariable(expr)) return expr.Part.DataType;
  if (isCastFn(expr)) return mapCastTargetToColumnDataType(expr.Part.TargetType);

  // noinspection JSUnusedLocalSymbols
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  /**
   * Type-exhaustiveness check breaks if a newly added expression type/subtype is not handled by this function.
   * If it is breaking, fix it by creating its own `isType(expr)` function and calling it here.
   * See https://www.typescriptlang.org/docs/handbook/2/narrowing.html#exhaustiveness-checking.
   */
  const _: never = expr;
  return ColumnDataTypes.Int;
};

// Formatter-side ColumnDataType per Cast target. Enum-keyed so omissions are compile errors.
// Date and DateTime both collapse to DateTime; isDateOnlyExpression picks the MDY date-only
// formatter for Cast-to-Date results.
const castTargetToColumnDataTypeMap: Record<CastTargetTypes, ColumnDataTypes> = {
  [CastTargetTypes.Integer]: ColumnDataTypes.Int,
  [CastTargetTypes.Decimal]: ColumnDataTypes.Decimal,
  [CastTargetTypes.String]: ColumnDataTypes.String,
  [CastTargetTypes.Bool]: ColumnDataTypes.Boolean,
  [CastTargetTypes.Date]: ColumnDataTypes.DateTime,
  [CastTargetTypes.DateTime]: ColumnDataTypes.DateTime,
};

// Int fallback is unreachable under normal flow (parser rejects unknown targets); it keeps a
// corrupted stored expression degrading gracefully instead of throwing in the render path.
const mapCastTargetToColumnDataType = (targetType: string): ColumnDataTypes => {
  const target = resolveCastTargetType(targetType);
  return target ? castTargetToColumnDataTypeMap[target] : ColumnDataTypes.Int;
};

// True if the expression's effective output is date-only — picks the MDY formatter to avoid
// moment.tz()'s midnight-UTC shift. An If is date-only only if both branches resolve to Date,
// matching the ClickHouse promotion to DateTime otherwise.
export const isDateOnlyExpression = (expr: IExpression): boolean => {
  if (!expr) return false;
  const guarded = expr as IExpressionGuarded;
  if (isMax(guarded)) return isDateOnlyExpression(guarded.Part.MaxOf);
  if (isMin(guarded)) return isDateOnlyExpression(guarded.Part.MinOf);
  if (isMaxIf(guarded)) return isDateOnlyExpression(guarded.Part.ExpressionToMax);
  if (isMinIf(guarded)) return isDateOnlyExpression(guarded.Part.ExpressionToMin);
  if (isAny(guarded)) return isDateOnlyExpression(guarded.Part.Expression);
  if (isAnyHeavy(guarded)) return isDateOnlyExpression(guarded.Part.Expression);
  if (isAnyLast(guarded)) return isDateOnlyExpression(guarded.Part.Expression);
  if (isArgMax(guarded)) return isDateOnlyExpression(guarded.Part.Target);
  if (isArgMin(guarded)) return isDateOnlyExpression(guarded.Part.Target);
  if (isArgMaxIf(guarded)) return isDateOnlyExpression(guarded.Part.Target);
  if (isArgMinIf(guarded)) return isDateOnlyExpression(guarded.Part.Target);
  if (isFirstValue(guarded)) return isDateOnlyExpression(guarded.Part.Expression);
  if (isLastValue(guarded)) return isDateOnlyExpression(guarded.Part.Expression);
  if (isLead(guarded)) return isDateOnlyExpression(guarded.Part.Expression);
  if (isLag(guarded)) return isDateOnlyExpression(guarded.Part.Expression);
  if (isIf(guarded)) {
    return isDateOnlyExpression(guarded.Part.ThenPart) && isDateOnlyExpression(guarded.Part.ElsePart);
  }
  if (isCastFn(guarded)) {
    return resolveCastTargetType(guarded.Part.TargetType) === CastTargetTypes.Date;
  }
  return false;
};

// ─── Custom filter variable usage detection (custom-filter-expressions feature) ──────────────────
// Finds which measure/dimension expressions reference a custom filter variable, so the UI can flag
// "broken" expressions when the referenced definition is removed — either deleted or its UseVar flag
// unchecked (the backend resolves only UseVar=true definitions, so both leave a dangling reference).

// Memoizes, per expression tree, the custom filter definition IDs its CustomFilterVariableExpr nodes
// reference. Keyed by the jsonExpression OBJECT REFERENCE — a tree is immutable until re-authored, so
// a recompute re-walks only the changed tree (others are O(1) hits). Module-level singleton with WEAK
// keys: stale tree versions are reclaimed by GC automatically (no eviction, no leak).
const customFilterVariableIdCache = new WeakMap<IExpression, string[]>();

const isExpressionNode = (value: unknown): value is IExpression => (
  !!value && typeof value === 'object' && 'Type' in (value as object) && 'Part' in (value as object)
);

// Generic pre-order walk of an expression tree. Children are discovered structurally: any IExpression
// ({ Type, Part }) nested anywhere under `Part` — directly, inside arrays, or under nested objects
// like a window clause — is a child. This avoids hand-maintaining a child accessor per node type and
// stays correct as new node types are added.
export const walkExpression = (expr: unknown, visit: (node: IExpression) => void): void => {
  if (!isExpressionNode(expr)) return;
  visit(expr);
  walkExpressionPart(expr.Part, visit);
};

const walkExpressionPart = (value: unknown, visit: (node: IExpression) => void): void => {
  if (!value || typeof value !== 'object') return;
  if (isExpressionNode(value)) { walkExpression(value, visit); return; }
  if (Array.isArray(value)) { value.forEach((item) => walkExpressionPart(item, visit)); return; }
  Object.values(value).forEach((item) => walkExpressionPart(item, visit));
};

// Collects the (deduped) custom filter definition IDs referenced by CustomFilterVariableExpr nodes in
// the tree. Memoized by the expression's object reference (see customFilterVariableIdCache).
export const collectCustomFilterVariableIds = (expr: IExpression | undefined): string[] => {
  if (!isExpressionNode(expr)) return [];
  if (customFilterVariableIdCache.has(expr)) {
    return customFilterVariableIdCache.get(expr);
  }
  const ids: string[] = [];
  walkExpression(expr, (node) => {
    if (isCustomFilterVariable(node)) ids.push(node.Part.Value);
  });
  const uniqueCustomFilterVariableIds = Array.from(new Set(ids));
  customFilterVariableIdCache.set(expr, uniqueCustomFilterVariableIds);
  return uniqueCustomFilterVariableIds;
};

// Minimal structural shape of an expression-store entry — keeps this core util decoupled from the
// redux ICustomExpression type. The report-builder expressionStore is structurally compatible.
export interface IExpressionStoreEntry {
  displayName?: string;
  jsonExpression?: IExpression;
}
export type IExpressionStoreLike = Record<string, IExpressionStoreEntry | undefined>;

export interface IBrokenCustomFilterExpression {
  uniqueId: string;
  displayName: string;
  missingIds: string[]; // referenced custom filter IDs that are no longer valid variables
}

// Expressions whose referenced custom filter variable IDs are not all present in `validVariableIds`
// (the set of UseVar=true definition IDs). Such an expression is "broken": it references a custom
// filter that was deleted or had UseVar unchecked.
export const getBrokenCustomFilterExpressions = (
  expressionStore: IExpressionStoreLike | undefined,
  validVariableIds: Set<string>,
): IBrokenCustomFilterExpression[] => {
  const broken: IBrokenCustomFilterExpression[] = [];
  Object.entries(expressionStore || {}).forEach(([uniqueId, entry]) => {
    const referencedCustomFilterVariableIds = collectCustomFilterVariableIds(entry?.jsonExpression);
    if (!referencedCustomFilterVariableIds.length) return;
    const missingIds = referencedCustomFilterVariableIds.filter((id) => !validVariableIds.has(id));
    if (missingIds.length) broken.push({ uniqueId, displayName: entry?.displayName ?? '', missingIds });
  });
  return broken;
};

// Expressions that reference a specific custom filter definition ID — used to warn (warn-then-allow)
// before deleting that filter or unchecking its UseVar flag.
export const findExpressionsReferencingVariable = (
  expressionStore: IExpressionStoreLike | undefined,
  variableId: string,
): Array<{ uniqueId: string; displayName: string }> => {
  const result: Array<{ uniqueId: string; displayName: string }> = [];
  Object.entries(expressionStore || {}).forEach(([uniqueId, entry]) => {
    if (collectCustomFilterVariableIds(entry?.jsonExpression).includes(variableId)) {
      result.push({ uniqueId, displayName: entry?.displayName ?? '' });
    }
  });
  return result;
};

// A node is a window function if it is a pure window fn (RowNumber/Rank/Lead/Lag/First/LastValue)
const isWindowFnNode = (node: { Type?: ExpressionTypes; Part?: { Window?: unknown } }): boolean => node?.Type === ExpressionTypes.WindowFn
  || (node?.Type === ExpressionTypes.Aggregation && Boolean(node?.Part?.Window));

export const containsWindowFn = (node: unknown): boolean => {
  if (!node || typeof node !== 'object') return false;
  if (isValidArray(node)) return node.some(containsWindowFn);
  if (isWindowFnNode(node as { Type?: ExpressionTypes })) return true;
  return Object.values(node).some(containsWindowFn);
};

export const firstWindowFnName = (node: unknown): string => {
  if (!node || typeof node !== 'object') return '';
  if (isValidArray(node)) return node.reduce<string>((acc, n) => acc || firstWindowFnName(n), '');
  if (isWindowFnNode(node as { Type?: ExpressionTypes })) return ((node as { SubType?: string }).SubType || '').replace(/Expr$/, '').replace(/Fn$/, '');
  return Object.values(node).reduce<string>((acc, n) => acc || firstWindowFnName(n), '');
};

// Checks whether a grouping candidate contains a window function.
// The expression is looked up from the candidate or the provided expression stores.
export const dimensionContainsWindowFn = (
  uniqueKey: string | number | undefined,
  fallbackExpression: unknown,
  ...expressionStores: Array<IExpressionStoreLike | undefined>
): boolean => {
  const storedExpression = expressionStores.reduce<IExpression | undefined>(
    (found, store) => found ?? store?.[uniqueKey as string]?.jsonExpression,
    undefined,
  );
  return containsWindowFn(storedExpression ?? fallbackExpression);
};
